import type { Adapter } from '@solana/wallet-adapter-base';
import {
  isVersionedTransaction,
  type SendTransactionOptions,
  type SignerWalletAdapter,
} from '@solana/wallet-adapter-base';
import type { Connection, Transaction, TransactionSignature, VersionedTransaction } from '@solana/web3.js';

function isUnsupportedSendError(error: unknown): boolean {
  const message = (error as Error)?.message?.toLowerCase() ?? '';
  return message.includes('not supported');
}

function isSignerWalletAdapter(adapter: Adapter): adapter is SignerWalletAdapter {
  return (
    'signTransaction' in adapter &&
    typeof (adapter as SignerWalletAdapter).signTransaction === 'function'
  );
}

export function adapterSupportsSending(adapter: Adapter): boolean {
  if (isSignerWalletAdapter(adapter)) return true;
  return (
    'sendTransaction' in adapter &&
    typeof (adapter as SignerWalletAdapter).sendTransaction === 'function'
  );
}

export async function sendTransactionWithAdapter(
  adapter: Adapter,
  transaction: Transaction | VersionedTransaction,
  connection: Connection,
  options: SendTransactionOptions = {}
): Promise<TransactionSignature> {
  if (
    'sendTransaction' in adapter &&
    typeof (adapter as SignerWalletAdapter).sendTransaction === 'function'
  ) {
    try {
      return await (adapter as SignerWalletAdapter).sendTransaction(transaction, connection, options);
    } catch (error) {
      if (!isUnsupportedSendError(error)) throw error;
    }
  }

  if (!isSignerWalletAdapter(adapter)) {
    throw new Error('This wallet does not support sending transactions');
  }

  const { signers, ...sendOptions } = options;
  let tx = transaction;

  if (isVersionedTransaction(tx)) {
    if (signers?.length) tx.sign(signers as never);
  } else if ('prepareTransaction' in adapter && typeof adapter.prepareTransaction === 'function') {
    tx = await adapter.prepareTransaction(tx, connection, sendOptions);
    if (signers?.length) (tx as Transaction).partialSign(...signers);
  } else {
    if (signers?.length) (tx as Transaction).partialSign(...signers);
  }

  const signed = await adapter.signTransaction(tx);
  const raw = signed.serialize();

  return connection.sendRawTransaction(raw, {
    skipPreflight: sendOptions.skipPreflight,
    preflightCommitment: sendOptions.preflightCommitment ?? connection.commitment,
    maxRetries: sendOptions.maxRetries,
    minContextSlot: sendOptions.minContextSlot,
  });
}
